/**
 * Whether squash ratings should be shown, and where.
 *
 * Mirrors the Flutter app's `squash_level_visibility.dart` — change both
 * together. The two clients showing a different answer for the same player is
 * the failure this pairing exists to prevent.
 *
 * A rating belongs to a squash COMPETITION, never to a club or a person. A
 * player who plays squash elsewhere carries a genuine rating everywhere, so the
 * player's own data can never decide this: the sport being looked at does.
 */
export function useSquashLevels() {
  /**
   * Whether a competition's sport uses ratings.
   *
   * FAILS CLOSED. An absent sport means the competition hasn't told us what it
   * is — still loading, or an older payload. Briefly showing nothing is
   * recoverable; showing a squash rating on a tennis ladder is a visible error.
   */
  const sportUsesSquashLevels = (sport) => sport?.squashLevels === true;

  /**
   * Whether to show a rating for one player. Both halves must hold: the
   * competition uses ratings, and this player has one. Negative ratings are
   * treated as absent — the API uses them as a not-yet-rated marker.
   */
  const showSquashLevel = (sport, squashLevel) =>
      showSquashLevelWhen(sportUsesSquashLevels(sport), squashLevel);

  /**
   * The same rule for components handed the sport's ANSWER rather than the
   * sport. Shared tables (standings) are used by leagues, tournament groups and
   * the feed, so the caller resolves it once and passes a boolean — but the
   * decision still lives here.
   */
  const showSquashLevelWhen = (sportUsesLevels, squashLevel) =>
      sportUsesLevels === true && squashLevel != null && squashLevel >= 0;

  return {sportUsesSquashLevels, showSquashLevel, showSquashLevelWhen};
}
